/**
 * Tasker API service — vendor-side wizard endpoints.
 *
 * Covers the five endpoints the vendor onboarding flow needs against the
 * generic Tasker API surface (per ADR-0028 runbook):
 *
 *   getTask(taskId)                        → GET  /tasks/{id}
 *   getTaskType(taskTypeId)                → GET  /task-types/{id}
 *   getFormSchema(formId, taskId?)         → GET  /forms/{id}?taskId=
 *   saveStepFormData(formId, taskId, data) → POST /form-data
 *   executeTransition(taskId, action, ...) → POST /tasks/{id}/transitions
 *
 * All calls use `useVendorToken: true` — the api.js interceptor picks up
 * the Tasker JWT from sessionStorage.VENDOR_TASKER_TOKEN.
 *
 * The two-call vendor-step save pattern (per user decision 2026-07-13):
 * FE calls `saveStepFormData(...)` to persist the step's form_data row,
 * THEN calls `executeTransition(taskId, "save_step_1")` with no form_data
 * in the body to advance state. Reviewer transitions instead use the
 * ADR-0070 widened single-call body with form_data on the transition.
 */

import api from './api';
import type {
  FormMetadata,
  FormSchemaApiResponse,
  ProcessedFormField,
  ScopeBundle,
  TaskerFormField,
} from '@/types/api/formSchema';
import {
  ensureTaskerAuth,
  taskerCfg,
  type TaskerAuthMode,
} from './taskerConfig';

export type { TaskerAuthMode };

// ─── Types ───────────────────────────────────────────────────────────────────

export type TaskerAssignee = {
  id: string;
  role?: string;
  name?: string;
  email?: string;
};

/** Tasker task shape — loose to accept unknown backend fields. */
export type TaskerTask = {
  id: string;
  task_type_id?: string;
  current_status: string;
  request_data?: Record<string, unknown>;
  owner_reference?: Record<string, unknown>;
  task_visible_id?: string;
  assignee_ids?: TaskerAssignee[];
  [key: string]: unknown;
};

/** Task-type config that exposes step_forms / transition_forms maps (ADR-0028). */
export type TaskerTaskConfig = {
  step_forms?: Record<string, string>;
  transition_forms?: Record<string, string>;
  total_steps?: number;
  [key: string]: unknown;
};

export type TaskerTaskType = {
  id: string;
  name: string;
  description?: string;
  state_machine?: { id: string; [key: string]: unknown };
  task_request_form_id?: string;
  task_response_form_id?: string;
  task_config?: TaskerTaskConfig;
  [key: string]: unknown;
};

/** Response of the two form-data-related endpoints. */
export type TaskerFormDataRow = {
  id?: string;
  form_id?: string;
  task_id?: string;
  form_data: Record<string, unknown>;
  [key: string]: unknown;
};

/** Result of getFormSchema: display-ready fields + optional scope bundle + form_metadata. */
export type FormSchemaResult = {
  fields: ProcessedFormField[];
  scope?: ScopeBundle;
  metadata?: FormMetadata;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Convert `snake_case` / `camelCase` to a display label. */
function camelCaseToLabel(name: string): string {
  return name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Unwrap the standard Tasker envelope `{data: [...], status: {...}}` and
 * return the first element. Returns null when the envelope is missing
 * or the data array is empty — callers should handle that case.
 */
function unwrapFirst<T>(response: unknown): T | null {
  const body = response as { data?: T[] } | null | undefined;
  const first = body?.data?.[0];
  return (first as T) ?? null;
}

// ─── API functions ───────────────────────────────────────────────────────────

/** GET /tasks/{taskId} → the task or null if not found in envelope. */
export async function getTask(
  taskId: string,
  authMode: TaskerAuthMode = 'vendor',
): Promise<TaskerTask | null> {
  await ensureTaskerAuth(authMode);
  const response = await api.get(
    `/tasks/${encodeURIComponent(taskId)}`,
    taskerCfg(authMode),
  );
  return unwrapFirst<TaskerTask>(response);
}

/** GET /task-types/{taskTypeId} → the task type or null. */
export async function getTaskType(
  taskTypeId: string,
  authMode: TaskerAuthMode = 'vendor',
): Promise<TaskerTaskType | null> {
  await ensureTaskerAuth(authMode);
  const response = await api.get(
    `/task-types/${encodeURIComponent(taskTypeId)}`,
    taskerCfg(authMode),
  );
  return unwrapFirst<TaskerTaskType>(response);
}

/**
 * GET /forms/{formId}[?taskId=Y]
 *
 * Returns processed fields sorted by tabIndex and filtered to visible
 * (tabIndex > -1). When taskId is provided, the backend response includes
 * a scope bundle for JEXL evaluation — attached to the result under `scope`.
 *
 * Adapted from canvas-web-v2's getFormSchema. The scope lives at the
 * response root per ADR-0070 §13, NOT inside data[0].
 */
export async function getFormSchema(
  formId: string,
  taskId?: string,
  authMode: TaskerAuthMode = 'vendor',
): Promise<FormSchemaResult> {
  await ensureTaskerAuth(authMode);
  const query = taskId ? `?taskId=${encodeURIComponent(taskId)}` : '';
  const response = (await api.get(
    `/forms/${encodeURIComponent(formId)}${query}`,
    taskerCfg(authMode),
  )) as FormSchemaApiResponse | null;

  const entry = response?.data?.[0];
  if (!entry?.schema || !Array.isArray(entry.schema)) {
    return { fields: [] };
  }

  const process = (field: TaskerFormField): ProcessedFormField => {
    const processed: ProcessedFormField = {
      ...field,
      resolvedLabel: field.display_name || field.label || camelCaseToLabel(field.name),
    };
    if (field.type === 'array' && Array.isArray(field.fields)) {
      processed.fields = (field.fields as TaskerFormField[]).map(process);
    }
    return processed;
  };

  const fields: ProcessedFormField[] = entry.schema
    .filter((field: TaskerFormField) => (field.tabIndex ?? 0) > -1)
    .sort((a: TaskerFormField, b: TaskerFormField) => (a.tabIndex ?? 0) - (b.tabIndex ?? 0))
    .map(process);

  return {
    fields,
    scope: response?.scope,
    metadata: entry.form_metadata,
  };
}

/**
 * POST /form-data
 * Persists a step's form_data row. Vendor step Next click = this call
 * FOLLOWED by a plain-body executeTransition (2-call save pattern).
 */
export async function saveStepFormData(
  formId: string,
  taskId: string,
  formData: Record<string, unknown>,
  authMode: TaskerAuthMode = 'vendor',
): Promise<TaskerFormDataRow> {
  await ensureTaskerAuth(authMode);
  const body = {
    form: { id: formId },
    task: { id: taskId },
    form_data: formData,
  };
  const response = (await api.post(
    '/form-data',
    body,
    taskerCfg(authMode),
  )) as TaskerFormDataRow;
  return response;
}

export async function updateFormData(
  id: string,
  formData: Record<string, unknown>,
  authMode: TaskerAuthMode = 'vendor',
): Promise<TaskerFormDataRow> {
  await ensureTaskerAuth(authMode);
  const response = (await api.put(
    `/form-data/${encodeURIComponent(id)}`,
    { form_data: formData },
    taskerCfg(authMode),
  )) as TaskerFormDataRow;
  return response;
}

export async function upsertStepFormData(
  formId: string,
  taskId: string,
  formData: Record<string, unknown>,
  authMode: TaskerAuthMode = 'vendor',
): Promise<TaskerFormDataRow> {
  const rows = await searchFormData(taskId, formId, authMode);
  if (rows.length === 0) {
    return saveStepFormData(formId, taskId, formData, authMode);
  }
  const latest = [...rows].sort((a, b) => {
    const ta = new Date((a.created_at as string) ?? 0).getTime();
    const tb = new Date((b.created_at as string) ?? 0).getTime();
    return tb - ta;
  })[0];
  const id = latest?.id;
  if (!id) {
    return saveStepFormData(formId, taskId, formData, authMode);
  }
  return updateFormData(id, formData, authMode);
}

/**
 * POST /form-data/search — canvas-web-v2 pattern for prior-step lookups.
 * Returns the persisted form_data rows matching the filter. In the wizard
 * we use this on the review step to fetch each earlier step's saved data.
 */
export async function searchFormData(
  taskId: string,
  formId: string,
  authMode: TaskerAuthMode = 'vendor',
  configOverrides?: Record<string, unknown>,
): Promise<TaskerFormDataRow[]> {
  await ensureTaskerAuth(authMode);
  const response = (await api.post(
    '/form-data/search',
    {
      filters: `formId.eq:${formId};taskId.eq:${taskId}`,
      sort_by: '',
      page: 0,
      fetch_size: 20,
      includes: '',
    },
    { ...taskerCfg(authMode), ...configOverrides },
  )) as { data?: TaskerFormDataRow[] } | null;
  return response?.data ?? [];
}

/**
 * POST /tasks/{taskId}/transitions
 * Advances the task's state via the given action.
 *
 * For vendor step saves, call without formData (data was persisted via
 * saveStepFormData first). For reviewer transitions (per ADR-0070's
 * widened body), pass formData and Tasker will persist + transition
 * atomically. Pre-hooks fire synchronously — validation failures come
 * back as 4xx errors.
 */
export async function executeTransition(
  taskId: string,
  action: string,
  formData?: Record<string, unknown>,
  authMode: TaskerAuthMode = 'vendor',
): Promise<TaskerTask | null> {
  await ensureTaskerAuth(authMode);
  const body: Record<string, unknown> = { action };
  if (formData && Object.keys(formData).length > 0) {
    body.form_data = formData;
  }
  const response = await api.post(
    `/tasks/${encodeURIComponent(taskId)}/transitions`,
    body,
    taskerCfg(authMode),
  );
  return unwrapFirst<TaskerTask>(response);
}

// ─── Default export ──────────────────────────────────────────────────────────

const taskerService = {
  getTask,
  getTaskType,
  getFormSchema,
  saveStepFormData,
  updateFormData,
  upsertStepFormData,
  searchFormData,
  executeTransition,
};

export default taskerService;
